import AddIcon from "@mui/icons-material/Add";
import { Button, Checkbox, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { cloneDeep, isArray } from "lodash";
import { memo, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AsyncSelect, {
  AsyncSelectValue,
} from "../../../../../../../components/AsyncSelect";
// Specialty filter removed from PE import — CustomSelect no longer needed
// import CustomSelect from "../../../../../../../components/CustomSelect";
import CustomTextArea from "../../../../../../../components/CustomTextArea";
import { FlexCenterWrapper } from "../../../../../../../components/DefaultNoDataWrapper";
import Flex from "../../../../../../../components/Flex";
import Text from "../../../../../../../components/Text";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../../../hooks/use-debounce";
import useDisclosure, {
  DisclosureHost,
} from "../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useRosPeTemplateCreate } from "../../../../../../../queries/encounter/use-ros-pe-template-create";
import { useRosPeTemplateUpdate } from "../../../../../../../queries/encounter/use-ros-pe-template-update";
import { useRosTemplateById } from "../../../../../../../queries/template/new-flow/use-new-template-by-id-list";
import { useNewTemplateList } from "../../../../../../../queries/template/new-flow/use-new-template-list";
// Specialty filter removed from PE import
// import { useSpecialtiesList } from "../../../../../../../queries/use-specialities-list";
import {
  DEF_PAGINATION,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../utils/utils";
import { PeAddTemplateDrawer } from "../../../../../admin/library/templates/RosTemplateDrawer";
import { newPeTemplateInitialData } from "../../../../../admin/library/templates/template-constant";
import { RosAndPe } from "../encounter-constant";

interface RosImportDrawerProps {
  disclosureHost: DisclosureHost;
}

const PeImportDrawer = memo(({ disclosureHost }: RosImportDrawerProps) => {
  const { mutateAsync: createPeTemplate } = useRosPeTemplateCreate();
  const { mutateAsync: updatePeTemplate } = useRosPeTemplateUpdate();
  const appointmentId = useQueryParams("appointmentid");
  const { patientId: routePatientId } = useParams();
  const queryPatientId = useQueryParams("patientid");
  const patientId = queryPatientId || routePatientId;

  const queryClient = useQueryClient();

  const peTemplateDisc = useDisclosure();
  const [pe, setPe] = useState<RosAndPe[] | null>(null);
  const showSnackbar = useSnackbar();
  const [selectedTemplate, setSelectedTemplate] =
    useState<AsyncSelectValue | null>();
  // Specialty filter removed from PE import
  // const [speciality, setSpeciality] = useState<string | null>();
  const [searchKey, setSearchKey] = useDebounce<string>("");
  // const [specialitySearch, setSpecialitySearch] = useDebounce<string>("");

  // const { data: specialities } = useSpecialtiesList({
  //   searchString: specialitySearch,
  //   xTENANTID: getXtenantId(),
  // });

  // const specialtiesList = useMemo(
  //   () =>
  //     specialities?.data?.content?.map((it) => ({
  //       value: it?.uuid,
  //       label: it?.name,
  //     })),
  //   [specialities]
  // );

  const { data, isLoading, isRefetching } = useNewTemplateList({
    enabled: disclosureHost.opened,
    pagination: DEF_PAGINATION,
    gridSorting: [],
    searchKey,
    templateType: "PHYSICAL",
    // specialityUuids: speciality ? [speciality] : undefined,
    xTENANTID: getXtenantId(),
  });

  const { data: templateByIdData } = useRosTemplateById({
    rosTemplateUuid: selectedTemplate?.value,
    xTENANTID: getXtenantId(),
  });

  useEffect(() => {
    if (selectedTemplate?.value) {
      setPe(
        templateByIdData?.data?.rosTemplateTypeSet?.map((it: any) => ({
          templateType: "PHYSICAL_EXAM",
          templateNameType: it?.name,
          description: it?.value,
          isSelected: it?.isSelected,
        })) ?? null
      );
    } else if (disclosureHost?.data && isArray(disclosureHost.data)) {
      setPe(disclosureHost?.data?.map((it) => ({ ...it, isSelected: true })));
    } else if (disclosureHost?.data) {
      setPe([{ ...disclosureHost?.data, isSelected: true }]);
    }
  }, [disclosureHost.opened, templateByIdData]);

  const handleSubmit = () => {
    const payload = {
      physicalROSTemplateRequest: {
        appointmentId: appointmentId ?? undefined,
        patientId: patientId ?? undefined,
        physicalROSTemplateList: pe?.filter((it) => !!it.isSelected),
      },
    };

    (disclosureHost?.data || !isArray(disclosureHost?.data)
      ? updatePeTemplate(payload)
      : createPeTemplate(payload)
    )
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({
          queryKey: ["patient-ros-pe-template-list"],
        });

        closeAndReset();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const handleSelect = (ind: number) => {
    const copiedRos = cloneDeep(pe);
    if (copiedRos) {
      copiedRos[ind].isSelected = !copiedRos[ind].isSelected;
      setPe(copiedRos);
    }
  };

  const onDescriptionChange = (v: string, ind: number) => {
    const copiedRos = cloneDeep(pe);
    if (copiedRos) {
      copiedRos[ind].description = v;
      setPe(copiedRos);
    }
  };

  const closeAndReset = () => {
    setPe(null);
    setSelectedTemplate(null);
    // setSpeciality(null);
    disclosureHost.close();
  };

  const rosTemplateList = useMemo(() => {
    return data?.data?.content?.map((it) => ({
      value: it.uuid,
      label: it?.templateName,
    }));
  }, [data]);

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndReset}
      dialogTitle="Add PE"
      dialogAction={
        <Flex gap={1}>
          <StyledButtonV2
            label="Save"
            variant="contained"
            onClick={handleSubmit}
          />
          <StyledButtonV2
            label="Cancel"
            variant="outlined"
            onClick={closeAndReset}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "50vw",
          height: "65vh",
        },
      }}
    >
      <Flex flexDirection={"column"} gap={1} width={"100%"} pt={2}>
        {(isArray(disclosureHost.data) || !disclosureHost?.data) && (
          <Grid container spacing={1}>
            {/* Specialty filter removed from PE import */}
            {/* <Grid item xs={4}>
              <CustomSelect
                label="Speciality"
                placeholder="Speciality"
                options={specialtiesList}
                onSearch={(_, val) => setSpecialitySearch(val)}
                value={speciality}
                onChange={(v) => setSpeciality(v)}
              />
            </Grid> */}
            <Grid size={11}>
              <AsyncSelect
                loading={isLoading || isRefetching}
                placeholder="Select Template"
                value={selectedTemplate}
                options={rosTemplateList}
                onSearch={setSearchKey}
                onChange={(v) => setSelectedTemplate(v)}
              />
            </Grid>

            <Grid size={1}>
              <Button
                sx={{ marginLeft: 0.5, marginTop: 1 }}
                variant="contained"
                onClick={() =>
                  peTemplateDisc.open({
                    templateTypes: "PHYSICAL",
                    rosTemplateTypeSet: newPeTemplateInitialData,
                  })
                }
              >
                <AddIcon />
              </Button>
            </Grid>
            <PeAddTemplateDrawer disclosureHost={peTemplateDisc} />
          </Grid>
        )}
        {((!pe?.length && !disclosureHost?.data) ||
          (isArray(disclosureHost?.data) &&
            !disclosureHost?.data?.length &&
            !pe?.length)) && (
          <Flex height={"60vh"}>
            <FlexCenterWrapper>{"Please Select Template"}</FlexCenterWrapper>
          </Flex>
        )}
        <Flex flexDirection={"column"} gap={1}>
          {pe?.map(
            (it, ind) =>
              it?.isSelected && (
                <Flex key={ind} gap={2} alignItems={"start"}>
                  <Checkbox
                    checked={it.isSelected}
                    onChange={() => handleSelect(ind)}
                  />
                  <Flex flexDirection={"column"} width={"100%"} mt={1.5}>
                    <Text>{it.templateNameType}</Text>
                    {it.isSelected && (
                      <CustomTextArea
                        id="outlined-multiline-static"
                        variant="outlined"
                        name="desc"
                        placeholder="Enter Description"
                        value={it?.description}
                        onChange={(e) => {
                          onDescriptionChange(e.target.value, ind);
                        }}
                      />
                    )}
                  </Flex>
                </Flex>
              )
          )}
        </Flex>
      </Flex>
    </StyledFormModal>
  );
});

export default PeImportDrawer;
